const sum = (rows, field) => rows.reduce((total, row) => total + row[field], 0);
const close = (left, right) => Math.abs(left - right) <= Math.max(0.001, Math.abs(right) * 1e-7);
const validDay = (day) => day.hasDispatch && Number.isFinite(day.classifiedDistanceKm) && day.classifiedDistanceKm > 0
  && Number.isFinite(day.lowLoadDistanceKm);

function mergeFactors(days, field, totalKm, lowKm) {
  const grouped = new Map();
  for (const day of days) for (const row of day[field]) {
    const total = grouped.get(row.key) || { ...row, distanceKm: 0, lowLoadDistanceKm: 0, emptyDistanceKm: 0 };
    for (const key of ["distanceKm", "lowLoadDistanceKm", "emptyDistanceKm"]) total[key] += row[key];
    grouped.set(row.key, total);
  }
  return [...grouped.values()].map((row) => ({ ...row,
    contributionPts: row.lowLoadDistanceKm / totalKm * 100,
    sharePct: lowKm > 0 ? row.lowLoadDistanceKm / lowKm * 100 : 0,
  })).sort((a, b) => b.lowLoadDistanceKm - a.lowLoadDistanceKm || a.name.localeCompare(b.name, "fr", { numeric: true }));
}

export function summarizeDashboardLowLoadInfluence(days = []) {
  const validDays = days.filter(validDay);
  const totalKm = sum(validDays, "classifiedDistanceKm");
  const lowKm = sum(validDays, "lowLoadDistanceKm");
  const factorDays = validDays.filter((day) => Array.isArray(day.lowLoadFactors)
    && close(sum(day.lowLoadFactors, "distanceKm"), day.classifiedDistanceKm)
    && close(sum(day.lowLoadFactors, "lowLoadDistanceKm"), day.lowLoadDistanceKm));
  const siteDays = factorDays.filter((day) => {
    if (!Array.isArray(day.lowLoadLoadingSites)) return false;
    const approaches = day.lowLoadFactors.find((row) => row.key === "loading");
    return close(sum(day.lowLoadLoadingSites, "distanceKm"), approaches?.distanceKm || 0)
      && close(sum(day.lowLoadLoadingSites, "lowLoadDistanceKm"), approaches?.lowLoadDistanceKm || 0);
  });
  const complete = validDays.length > 0 && factorDays.length === validDays.length;
  const sitesComplete = validDays.length > 0 && siteDays.length === validDays.length;
  const pct = totalKm > 0 ? lowKm / totalKm * 100 : null;
  return {
    totalKm, lowKm, pct, complete, sitesComplete,
    validDayCount: validDays.length, factorDayCount: factorDays.length, siteDayCount: siteDays.length,
    excludedTripCount: days.filter((day) => day.hasDispatch).reduce((total, day) => total
      + (day.lowLoadTrucks || []).reduce((count, truck) => count + truck.excludedTripCount, 0), 0),
    factors: mergeFactors(factorDays, "lowLoadFactors", totalKm, lowKm),
    sites: mergeFactors(siteDays, "lowLoadLoadingSites", totalKm, lowKm).map((site) => {
      const remainingKm = totalKm - site.distanceKm;
      const withoutPct = sitesComplete && remainingKm > 1e-9 ? Math.max(0, lowKm - site.lowLoadDistanceKm) / remainingKm * 100 : null;
      return { ...site, withoutPct, changePts: withoutPct === null ? null : withoutPct - pct };
    }),
  };
}

function contributionChanges(current, previous) {
  const before = new Map(previous.map((row) => [row.key, row]));
  const now = new Map(current.map((row) => [row.key, row]));
  return [...new Set([...now.keys(), ...before.keys()])].map((key) => ({
    ...(now.get(key) || before.get(key)),
    deltaPts: (now.get(key)?.contributionPts || 0) - (before.get(key)?.contributionPts || 0),
  }));
}

export function analyzeDashboardLowLoadInfluence(current, previous) {
  const dominant = current.complete && current.lowKm > 0 ? current.factors[0] : null;
  if (!current.complete || !previous?.complete) return { dominant, trend: null };
  const deltaPts = current.pct - previous.pct;
  const changes = contributionChanges(current.factors, previous.factors).sort((a, b) => Math.abs(b.deltaPts) - Math.abs(a.deltaPts));
  const stable = Math.abs(deltaPts) < 0.05;
  const leading = changes.find((row) => Math.abs(row.deltaPts) >= 0.05 && (stable || row.deltaPts * deltaPts > 0)) || null;
  const offset = leading ? changes.find((row) => row.deltaPts * leading.deltaPts < 0 && Math.abs(row.deltaPts) >= 0.05) || null : null;
  const site = leading?.key === "loading" && current.sitesComplete && previous.sitesComplete
    ? contributionChanges(current.sites, previous.sites).filter((row) => row.deltaPts * leading.deltaPts > 0 && Math.abs(row.deltaPts) >= 0.05)
      .sort((a, b) => Math.abs(b.deltaPts) - Math.abs(a.deltaPts))[0] || null : null;
  return { dominant, trend: { deltaPts, stable, leading, offset, site } };
}
